(function() {
  const drawer = document.querySelector('.header__drawer[data-hydration-key]');
  if (!drawer) return;

  // The header section re-hydrates itself via the Section Rendering API
  // shortly after page load (see hydrate() call in sections/header.liquid).
  // That morph replaces/updates everything inside .header__drawer (the
  // data-hydration-key target) with fresh server-rendered markup — which
  // wipes out .drawer-nav-list (JS-built, empty from Liquid) and can swap
  // out .drawer-panels-container itself, invalidating any saved reference
  // to it. So: never cache child element references — always re-query from
  // `drawer` at the moment each function runs, and rebuild on every mutation
  // inside `drawer`, not just once.
  const transitionTime = 300;
  let isAnimating = false;
  let pendingTabTimeout = null;

  function buildNavList() {
    const navList = drawer.querySelector('.drawer-nav-list');
    const panelsContainer = drawer.querySelector('.drawer-panels-container');
    if (!navList || !panelsContainer) return;

    const panels = [...panelsContainer.querySelectorAll(':scope > .drawer-panel')];

    // The header re-hydrates a second or two after load, and this runs again
    // for every mutation it causes. Wiping and re-appending identical items
    // would empty the nav for a frame — visible as a flicker if the drawer is
    // already open — so bail out when the rendered tabs already match.
    const wanted = panels.map((panel) => panel.dataset.tabName).filter(Boolean);
    const current = [...navList.children].map((item) => item.dataset.target);
    if (wanted.length === current.length && wanted.every((name, i) => name === current[i])) return;

    navList.innerHTML = '';
    panels.forEach(panel => {
      const name = panel.dataset.tabName;
      const label = panel.dataset.tabLabel || name;
      if (!name) return;

      const navItem = document.createElement('div');
      navItem.className = 'drawer-nav-item';
      navItem.dataset.target = name;
      navItem.textContent = label.toUpperCase();
      navList.appendChild(navItem);

      navItem.addEventListener('click', () => switchTab(navItem));
    });
  }

  function switchTab(clickedItem) {
    if (isAnimating || clickedItem.classList.contains('active')) return;

    const navList = drawer.querySelector('.drawer-nav-list');
    const panelsContainer = drawer.querySelector('.drawer-panels-container');
    if (!navList || !panelsContainer) return;

    isAnimating = true;
    const newPanel = panelsContainer.querySelector(`.drawer-panel[data-tab-name="${clickedItem.dataset.target}"]`);
    const currentItem = navList.querySelector('.drawer-nav-item.active');
    const currentPanel = panelsContainer.querySelector('.drawer-panel.is-visible');

    if (currentItem) currentItem.classList.remove('active');
    clickedItem.classList.add('active');

    const targetScroll = clickedItem.offsetLeft - (navList.clientWidth / 2) + (clickedItem.offsetWidth / 2);
    navList.scrollTo({ left: Math.max(0, Math.min(targetScroll, navList.scrollWidth - navList.clientWidth)), behavior: 'smooth' });

    if (currentPanel) {
      currentPanel.classList.remove('is-visible');

      pendingTabTimeout = setTimeout(() => {
        pendingTabTimeout = null;
        currentPanel.classList.remove('is-open');

        if (newPanel) {
          newPanel.classList.add('is-open');
          requestAnimationFrame(() => {
            newPanel.classList.add('is-visible');
            setTimeout(() => { isAnimating = false; }, transitionTime);
          });
        } else {
          isAnimating = false;
        }
      }, transitionTime);
    } else {
      if (newPanel) {
        newPanel.classList.add('is-open', 'is-visible');
      }
      isAnimating = false;
    }
  }

  function initActiveTab() {
    const navList = drawer.querySelector('.drawer-nav-list');
    const panelsContainer = drawer.querySelector('.drawer-panels-container');
    if (!navList || !panelsContainer) return;
    // buildNavList() rebuilds nav items from scratch on every refresh (e.g.
    // the hydration re-render), so an already-open panel doesn't mean the
    // freshly-built nav item still has .active — only skip if it does.
    if (navList.querySelector('.drawer-nav-item.active')) return;

    const openPanel = panelsContainer.querySelector('.drawer-panel.is-open');
    const preferredName = openPanel?.dataset.tabName || drawer.dataset.activeTab;
    let navItem = preferredName
      ? navList.querySelector(`.drawer-nav-item[data-target="${preferredName}"]`)
      : null;
    if (!navItem) navItem = navList.querySelector('.drawer-nav-item');
    if (!navItem) return;

    navItem.classList.add('active');
    const panel = panelsContainer.querySelector(`.drawer-panel[data-tab-name="${navItem.dataset.target}"]`);
    if (panel) panel.classList.add('is-open', 'is-visible');
  }

  /**
   * Which drawer link should light up for the product being viewed, in
   * priority order:
   *   1. menu.primary_collection (data-primary-url) — manual override, wins
   *      outright whenever it points at a link the drawer renders.
   *   2. A link matching one of the product's own collections; if several
   *      match, the most specific one (a parent of another match is dropped).
   *   3. A link whose group (data-parent-url) contains one of them.
   *
   * Exactly one link is ever returned. A product's collections are not a
   * chain: unisex items (socks, scarves, caps) sit in sibling collections
   * such as men-away-kit + kids-away-kit + women-away-kit, which have no
   * parent/child relation to each other, and the whole drawer would light
   * up. When the tie can't be broken by specificity, menu order decides —
   * `links` comes from querySelectorAll, so it already follows the
   * drawer-hierarchy order. Load menu.primary_collection on the product to
   * choose a different winner.
   */
  function matchProductLinks(links, pathOf) {
    const productPaths = drawer.dataset.activeUrls.split(',').filter(Boolean).map(pathOf);

    // Featured-image banners share the drawer-link data attributes so they can
    // open the right tab, but they are not .drawer-link-item children, so the
    // caller's highlight toggle can never find them. Since only one match is
    // returned now, a banner winning the tie would open the tab and leave
    // nothing highlighted — always prefer a real link when both match.
    const pick = (candidates) => candidates.find((link) => link.closest('.drawer-link-item')) || candidates[0];

    const primaryPath = drawer.dataset.primaryUrl ? pathOf(drawer.dataset.primaryUrl) : null;
    if (primaryPath) {
      // The same collection can be linked from more than one group or tab, so
      // narrow to the first of those in menu order rather than whichever the
      // DOM happened to yield first for an unconstrained lookup.
      const primaryLinks = links.filter((link) => pathOf(link.href) === primaryPath);
      if (primaryLinks.length) return [pick(primaryLinks)];
    }

    const exact = links.filter((link) => productPaths.includes(pathOf(link.href)));

    if (!exact.length) {
      const byParent = links.filter((link) => link.dataset.parentUrl && productPaths.includes(pathOf(link.dataset.parentUrl)));
      return byParent.length ? [pick(byParent)] : [];
    }

    if (exact.length === 1) return exact;

    // A product can genuinely belong to both a collection and its parent
    // (e.g. men-training-t-shirts and men-training) — prefer the more
    // specific one instead of highlighting both: drop any match that is
    // itself the parent of another match in the set.
    const narrowed = exact.filter((link) => {
      const ownPath = pathOf(link.href);
      return !exact.some((other) => other !== link && other.dataset.parentUrl && pathOf(other.dataset.parentUrl) === ownPath);
    });

    // Specificity can leave several unrelated siblings standing (and the
    // filter must never empty the set): menu order is the final tiebreak.
    return [pick(narrowed.length ? narrowed : exact)];
  }

  // The same collection can be linked from more than one tab (e.g.
  // women-home-kit lives under both KITS > WOMEN and WOMEN > KITS). Menu
  // order alone can't tell those apart, so remember which tab the user
  // actually clicked from and prefer a same-URL link that lives in that tab
  // on the page it navigates to. Read once and cleared immediately — it must
  // never linger and affect a later, unrelated visit to the same URL.
  const CLICKED_FROM_TAB_KEY = 'drawer-clicked-from-tab';

  function rememberClickedFromTab(linkEl) {
    const tabName = linkEl.closest('.drawer-panel')?.dataset.tabName;
    if (!tabName) return;
    try {
      sessionStorage.setItem(CLICKED_FROM_TAB_KEY, tabName);
    } catch (e) {
      // sessionStorage can throw in locked-down contexts (private mode,
      // disabled storage) — losing the hint just falls back to menu order.
    }
  }

  // Read and cleared exactly once per page load, at module init — not lazily
  // inside syncActiveFromLocation(), which the header's Section Rendering API
  // re-hydration calls again after the first paint. Consuming it there would
  // make only that first call see the hint and the re-hydration's call fall
  // back to menu order, flipping the active tab right after it was correct.
  let clickedFromTabForThisLoad = null;
  try {
    clickedFromTabForThisLoad = sessionStorage.getItem(CLICKED_FROM_TAB_KEY);
    sessionStorage.removeItem(CLICKED_FROM_TAB_KEY);
  } catch (e) {
    // ignore — falls back to menu order below
  }

  // Server-rendered active tab/link only reflect the URL/product at initial
  // page load. Soft navigations (e.g. collection-child-links tabs) change the
  // URL without reloading, so re-sync here whenever the drawer opens.
  function syncActiveFromLocation() {
    const navList = drawer.querySelector('.drawer-nav-list');
    if (!navList) return;

    // Featured Image banners (e.g. "NEW IN MEN") carry the same
    // data-parent-url/data-child-urls as drawer links so they can also open
    // the right tab, but aren't real nav items — excluded from the
    // drawer-link-item--active toggle below.
    const links = [...drawer.querySelectorAll('.drawer-link-item a[href], .drawer-featured-image[href]')];
    const pathOf = (href) => new URL(href, window.location.origin).pathname;

    let matchedLinks;

    if (drawer.dataset.activeUrls) {
      matchedLinks = matchProductLinks(links, pathOf);
    } else {
      const path = window.location.pathname;

      const candidates = links.filter(link => pathOf(link.href) === path);
      let matchedLink = candidates.length > 1 && clickedFromTabForThisLoad
        ? candidates.find(link => link.closest('.drawer-panel')?.dataset.tabName === clickedFromTabForThisLoad)
        : null;
      if (!matchedLink) matchedLink = candidates[0];

      if (!matchedLink) {
        matchedLink = links.find(link => (link.dataset.parentUrl && pathOf(link.dataset.parentUrl) === path));
      }
      if (!matchedLink) {
        matchedLink = links.find(link =>
          (link.dataset.childUrls || '').split(',').filter(Boolean).some(url => pathOf(url) === path)
        );
      }
      matchedLinks = matchedLink ? [matchedLink] : [];
    }

    drawer.querySelectorAll('.drawer-link-item').forEach(item => {
      item.classList.toggle('drawer-link-item--active', matchedLinks.includes(item.querySelector('a[href]')));
    });

    if (!matchedLinks.length) return;

    const targetPanel = matchedLinks[0].closest('.drawer-panel');
    if (!targetPanel) return;

    const targetTab = targetPanel.dataset.tabName;
    const navItem = navList.querySelector(`.drawer-nav-item[data-target="${targetTab}"]`);
    if (!navItem || navItem.classList.contains('active')) return;

    // After hydration the nav is rebuilt without any .active item, so the
    // "did the tab change?" check would see a change that isn't one and run
    // switchTab's fade — dropping .is-visible from an already-open panel and
    // flashing it back in. When the right panel is already showing, just
    // restore the nav highlight.
    if (targetPanel.classList.contains('is-open')) {
      navList.querySelector('.drawer-nav-item.active')?.classList.remove('active');
      navItem.classList.add('active');
      return;
    }

    switchTab(navItem);
  }

  function refresh() {
    // buildNavList() may rewrite .drawer-nav-list's children, which would
    // otherwise re-trigger the observer below (it watches drawer's whole
    // subtree) and loop forever. Pause it for the duration of our own writes.
    hydrationObserver.disconnect();
    buildNavList();
    syncActiveFromLocation();
    initActiveTab();
    hydrationObserver.observe(drawer, { childList: true, subtree: true });
  }

  // Rebuild synchronously inside the observer callback (a microtask, so it
  // still runs before the browser paints). Deferring to requestAnimationFrame
  // let the hydration morph's emptied nav list paint for one frame first,
  // which is the flicker users see when opening the drawer during load.
  const hydrationObserver = new MutationObserver(() => refresh());

  refresh();

  // Delegated on `drawer` (never rebuilt, unlike its children) so this
  // survives the hydration morph without needing to be reattached.
  drawer.addEventListener('click', (event) => {
    const link = event.target.closest('.drawer-link-item a[href]');
    if (link) rememberClickedFromTab(link);
  });

  const details = document.getElementById('Details-menu-drawer-container');
  if (details) {
    new MutationObserver(() => {
      if (details.classList.contains('menu-open')) {
        syncActiveFromLocation();
        initActiveTab();
      }
    }).observe(details, { attributes: true, attributeFilter: ['class'] });
  }
})();
